import { create } from 'zustand';
import { generateVerseInsights } from '../services/aiService';

interface VerseInfo {
  book: string;
  chapter: number;
  verse?: number;
  text: string;
  version: string;
}

interface InsightModalState {
  isOpen: boolean;
  verseInfo: VerseInfo | null;
  insights: string | null;
  isLoading: boolean;
  error: string | null;
  isDetailedInsight: boolean;
}

interface InsightsStore extends InsightModalState {
  openInsightModal: (verseInfoOrReference: VerseInfo | string) => void;
  closeInsightModal: () => void;
  generateInsights: () => Promise<void>;
  clearInsights: () => void;
  setDetailedInsight: (detailed: boolean) => void;
  navigateToChapter: (direction: number) => Promise<void>;
}

export const useInsightsStore = create<InsightsStore>((set, get) => ({
  isOpen: false,
  verseInfo: null,
  insights: null,
  isLoading: false,
  error: null,
  isDetailedInsight: false,

  openInsightModal: (verseInfoOrReference) => {
    let verseInfo: VerseInfo;
    
    if (typeof verseInfoOrReference === 'string') {
      const parts = verseInfoOrReference.split(' ');
      const book = parts[0];
      const [chapter, verse] = parts[1].split(':').map(Number);
      
      verseInfo = {
        book: book.toUpperCase(),
        chapter,
        verse: verse || undefined,
        text: '',
        version: 'kjv'
      };
    } else {
      verseInfo = {
        ...verseInfoOrReference,
        verse: verseInfoOrReference.verse === 0 ? undefined : verseInfoOrReference.verse
      };
    }

    set({
      isOpen: true,
      verseInfo,
      insights: null,
      isLoading: true,
      error: null
    });

    get().generateInsights();
  },

  closeInsightModal: () => set({ 
    isOpen: false, 
    verseInfo: null, 
    insights: null, 
    error: null,
    isLoading: false
  }),

  clearInsights: () => set({
    insights: null,
    error: null,
    isLoading: false
  }),

  setDetailedInsight: (detailed: boolean) => set({ isDetailedInsight: detailed }),

  navigateToChapter: async (direction: number) => {
    const state = get();
    if (!state.verseInfo) return;

    const currentChapter = state.verseInfo.chapter;
    const newChapter = currentChapter + direction;

    // Update verseInfo with new chapter
    const newVerseInfo = {
      ...state.verseInfo,
      chapter: newChapter,
      verse: 0, // Reset verse for chapter-level insight
    };

    // Update modal state
    set({
      verseInfo: newVerseInfo,
      insights: null,
      isLoading: true,
      error: null,
    });

    // Generate new insights
    try {
      const reference = `${newVerseInfo.book} ${newChapter}`;
      const insights = await generateVerseInsights(reference, '', true);
      set({ insights, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to generate insights',
        isLoading: false 
      });
    }
  },

  generateInsights: async () => {
    const state = get();
    if (!state.verseInfo) return;

    set({ isLoading: true, error: null });

    try {
      const reference = state.verseInfo.verse
        ? `${state.verseInfo.book} ${state.verseInfo.chapter}:${state.verseInfo.verse}`
        : `${state.verseInfo.book} ${state.verseInfo.chapter}`;

      const insights = await generateVerseInsights(
        reference,
        state.verseInfo.text,
        state.isDetailedInsight
      );

      set({ insights, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to generate insights',
        isLoading: false
      });
    }
  }
}));
